import { Chip, ChipRow, Field, Label } from "@/components/kit";
import { CAPACITY_FILTERS, formatGal } from "@/lib/tank";
import { Stepper } from "@/components/Stepper";
import { useT } from "@/lib/i18n";
import { StickPhoto } from "@/components/StickPhoto";

const FRACTIONS = [[".0", 0], ["¼", 0.25], ["½", 0.5], ["¾", 0.75]];

function StickInput({ testID, value, onValue, label }) {
  const whole = Math.floor(value + 1e-8);
  const frac = value - whole;
  return (
    <Field label={label}>
      <Stepper blank testID={testID} unit="in" decimal step={0.125} max={240} value={value} onValue={onValue} />
      <div className="frac-row">
        {FRACTIONS.map(([l, f]) => (
          <Chip key={l} testID={`${testID}-frac-${f}`} active={Math.abs(frac - f) < 0.01} onPress={() => onValue(whole + f)}>{l}</Chip>
        ))}
      </div>
      <StickPhoto testid={testID} onValue={onValue} />
    </Field>
  );
}

function Opts({ title, options, value, onPick, testPrefix }) {
  const t = useT();
  return (
    <div className="opt-block">
      <Label>{t(title)}</Label>
      <div className="opt-row">
        {options.map(([l, v]) => (
          <Chip key={String(v)} testID={`${testPrefix}-${v}`} active={value === v} onPress={() => onPick(v)}>{t(l)}</Chip>
        ))}
      </div>
    </div>
  );
}

const Gal = ({ testID, value, onValue }) => <Stepper blank testID={testID} unit="gal" step={100} max={80000} value={value} onValue={onValue} />;

export function ModeInputs({ s, patch }) {
  const m = s.solverMode;
  const t = useT();
  const set = (k) => (v) => patch({ [k]: v });
  if (m === "level") return (
    <div className="inputs">
      <StickInput testID="in-level-stick" label={t("Stick reading (inches)")} value={s.solverInches} onValue={set("solverInches")} />
      <Field label={t("Known gallons")}><Gal testID="in-level-gallons" value={s.solverGallons} onValue={set("solverGallons")} /></Field>
      <Label>{t("Nominal tank tag (optional)")}</Label>
      <ChipRow testID="cap-filter-row">
        {CAPACITY_FILTERS.map((c) => (
          <Chip key={c} testID={`cap-filter-${c}`} active={s.solverCap === c} onPress={() => patch({ solverCap: c })}>
            {c === "ANY" ? t("Any size") : `${formatGal(Number(c))} gal`}
          </Chip>
        ))}
      </ChipRow>
    </div>
  );
  if (m === "ullage") return (
    <div className="inputs">
      <StickInput testID="in-ullage-stick" label={t("Stick inches")} value={s.solverInches} onValue={set("solverInches")} />
      <Field label={t("Ullage gallons")}><Gal testID="in-ullage-gallons" value={s.solverGallons} onValue={set("solverGallons")} /></Field>
      <Opts title="That ullage is room" testPrefix="ullage-to" value={s.solverUllageTo} onPick={set("solverUllageTo")} options={[["To a full tank", "full"], ["To 90%", "90"]]} />
    </div>
  );
  if (m === "line") return (
    <div className="inputs">
      <Field label={t("Max fill, gallons")}><Gal testID="in-line-fill" value={s.solverFillLine} onValue={set("solverFillLine")} /></Field>
      <Field label={t("Ullage to that line")}><Gal testID="in-line-ullage" value={s.solverGallons} onValue={set("solverGallons")} /></Field>
      <Opts title="Those gallons are" testPrefix="fill-pct" value={s.solverFillPct} onPick={set("solverFillPct")} options={[["90%", 90], ["95%", 95], ["Top of tank", 100]]} />
    </div>
  );
  if (m === "drop") return (
    <div className="inputs">
      <div className="inputs inputs-tight">
        <Field label={t("Starting stick")}><Stepper blank testID="in-drop-start" unit="in" decimal step={0.125} max={240} value={s.solverStart} onValue={set("solverStart")} /><StickPhoto testid="in-drop-start" onValue={set("solverStart")} /></Field>
        <Field label={t("Ending stick")}><Stepper blank testID="in-drop-end" unit="in" decimal step={0.125} max={240} value={s.solverEnd} onValue={set("solverEnd")} /><StickPhoto testid="in-drop-end" onValue={set("solverEnd")} /></Field>
      </div>
      <Field label={t("Gallons delivered")}><Gal testID="in-drop-delivered" value={s.solverDelivered} onValue={set("solverDelivered")} /></Field>
    </div>
  );
  if (m === "receipt") return (
    <div className="inputs">
      <Field label={t("Height (inches)")}><Stepper blank testID="in-rcpt-height" unit="in" decimal step={0.125} max={240} value={s.rcptHeight} onValue={set("rcptHeight")} /><StickPhoto testid="in-rcpt-height" onValue={set("rcptHeight")} /></Field>
      <div className="inputs inputs-tight">
        <Field label={t("Volume, gallons")}><Gal testID="in-rcpt-volume" value={s.rcptVolume} onValue={set("rcptVolume")} /></Field>
        <Field label={t(s.rcptUllageKind === "90" ? "90% ullage, gallons" : "Ullage, gallons")}>
          <Gal testID="in-rcpt-ullage" value={s.rcptUllage} onValue={set("rcptUllage")} />
        </Field>
      </div>
      <Opts title="That ullage line is" testPrefix="rcpt-kind" value={s.rcptUllageKind} onPick={set("rcptUllageKind")} options={[["90% ullage", "90"], ["Ullage (to full)", "full"]]} />
    </div>
  );
  return (
    <div className="inputs">
      <Field label={t("Gallons in the tank")}><Gal testID="in-volume-gallons" value={s.solverGallons} onValue={set("solverGallons")} /></Field>
      <Field label={s.solverTankPct === 100 ? t("Tank size, gallons") : t("{p}% gallons", { p: s.solverTankPct })}>
        <Gal testID="in-volume-tank" value={s.solverTankGallons} onValue={set("solverTankGallons")} />
      </Field>
      <Opts title="That size is" testPrefix="tank-pct" value={s.solverTankPct} onPick={set("solverTankPct")} options={[["Full tank", 100], ["90%", 90], ["95%", 95]]} />
    </div>
  );
}
